import { Activity, CalendarEvent, SportType, PlannerProfile, PreferredDays, SPORTS } from "../types/training";

const PROFILE_KEY = "planner-profile";

// All derived from the SPORTS registry (types/training) — single source of truth.
export const ALL_SPORTS: SportType[] = SPORTS.map(s => s.value);

export const SPORT_LABELS = Object.fromEntries(SPORTS.map(s => [s.value, s.label])) as Record<SportType, string>;
export const SPORT_ABBREV = Object.fromEntries(SPORTS.map(s => [s.value, s.abbrev])) as Record<SportType, string>;

// "explosive" sports are excluded from the endurance volume ramp (TSS/CTL
// long-run model) and trained via strength/power/anaerobic work instead
// (strengthRoutines.ts + the capacity engine).
export const NON_ENDURANCE_SPORTS: SportType[] = SPORTS.filter(s => s.category === "explosive").map(s => s.value);
const ENDURANCE = new Set<SportType>(SPORTS.filter(s => s.category === "endurance").map(s => s.value));
export const isEnduranceSport = (s: SportType): boolean => ENDURANCE.has(s);
export const sportCategory = (s: SportType): "endurance" | "explosive" =>
  ENDURANCE.has(s) ? "endurance" : "explosive";

// Map raw type string to SportType
export function toSportType(raw: string): SportType | null {
  if (/^run$/i.test(raw))               return "Run";
  if (/ride|cycling/i.test(raw))        return "Ride";
  if (/nordicski|crosscountry/i.test(raw)) return "NordicSki";
  if (/roller.?ski/i.test(raw))         return "RollerSki";
  if (/weight|gym|strength/i.test(raw)) return "WeightTraining";
  if (/alpine|downhill|utf[öo]r/i.test(raw)) return "AlpineSki";
  return null;
}

// ── Duration parsing (mirrors WeeklyTSSChart logic) ───────────────────────────

function parseDurationHours(text: string): number {
  const t = text.toLowerCase();
  // Handles the compact "1h30m" form; excludes a preceding digit so the "0m"
  // inside "30m" isn't read as 0 minutes (see chartMetrics.parseDurationHours).
  const h = t.match(/(?<![a-z\d])(\d+(?:[.,]\d+)?)\s*h(?:ours?)?/);
  if (h) {
    const rest = t.slice(t.indexOf(h[0]) + h[0].length);
    const m2 = rest.match(/^\s*(\d+)\s*m(?:in)?/);
    return parseFloat(h[1].replace(",", ".")) + (m2 ? parseInt(m2[1]) / 60 : 0);
  }
  const m = t.match(/(?<![a-z\d])(\d+)\s*m(?:in)?\b/);
  if (m) return parseInt(m[1]) / 60;
  return 0;
}

function fmtHours(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  if (hrs > 0 && mins > 0) return `${hrs}h${mins}min`;
  if (hrs > 0) return `${hrs}h`;
  return `${mins}min`;
}

// ── Intensity detection ───────────────────────────────────────────────────────

function detectIntensity(text: string): string {
  const t = text.toLowerCase();
  if (/interval|intervall|\d+x\d+/.test(t))       return "intervaller";
  if (/tröskel|threshold/.test(t))                return "tröskel";
  if (/fartlek/.test(t))                          return "fartlek";
  if (/tempo/.test(t))                            return "tempo";
  if (/backe|hill/.test(t))                       return "backor";
  if (/pendling|commute/.test(t))                 return "pendling";
  if (/z4|z5|sprint|max/.test(t))                return "intervaller";
  if (/z3/.test(t))                               return "tempo";
  if (/z2|hr\b|heartrate|aerob/.test(t))         return "Z2";
  if (/z1|lätt|easy|lugn|recovery|vila/.test(t)) return "lätt";
  return "";
}

// ── Label builders ────────────────────────────────────────────────────────────

/** Build label from a PLANNED event — uses description for zone + duration info. */
function buildEventLabel(e: CalendarEvent, sport: SportType): string {
  const sLabel = SPORT_LABELS[sport].toLowerCase();
  const combined = `${e.name ?? ""} ${e.description ?? ""}`;
  const intensity = detectIntensity(combined);
  const hours = parseDurationHours(combined);
  const dur = hours > 0 ? fmtHours(hours) : "";

  if (intensity && dur) return `${sLabel} ${intensity} ${dur}`;
  if (intensity)        return `${sLabel} ${intensity}`;
  if (dur)              return `${sLabel} ${dur}`;
  return `${sLabel} pass`;
}

/** Fallback label from a completed ACTIVITY — uses duration when no description. */
function buildActivityLabel(a: Activity, sport: SportType): string {
  const sLabel = SPORT_LABELS[sport].toLowerCase();
  const combined = `${a.name ?? ""}`;
  const intensity = detectIntensity(combined);
  if (intensity) return `${sLabel} ${intensity}`;

  const hrs = a.moving_time / 3600;
  if (hrs < 0.5)  return `${sLabel} 30min`;
  if (hrs < 0.85) return `${sLabel} 45min`;
  if (hrs < 1.25) return `${sLabel} 1h`;
  if (hrs < 1.75) return `${sLabel} 1h30`;
  if (hrs < 2.5)  return `${sLabel} 2h`;
  if (hrs < 3.5)  return `${sLabel} 3h`;
  return `${sLabel} 4h+`;
}

// ── Detection: prioritise planned events, fall back to activities ─────────────

export interface WorkoutSuggestion {
  label: string;
  count: number;
  sport: SportType;
}

export interface DetectedSport {
  sport: SportType;
  count: number;    // based on activity history
  workouts: WorkoutSuggestion[];
}

const RACE_CAT = /^RACE(_[A-D])?$/i;

// Training-history analysis from up to a year of activities.
//  - weeklyHours: average over the last 6 weeks (current form / starting point)
//  - longestHours: longest single session of the sport over the year (informs
//    how far the long run can safely progress — you've done it before)
//  - peakWeeklyHours: average of the 3 biggest weeks over the year (demonstrated
//    sustainable capacity — lets the plan rebuild toward a volume you've held,
//    even if your recent weeks were quiet)
export function recentVolume(
  activities: Activity[],
  sport: SportType,
): { weeklyHours: number; longestHours: number; peakWeeklyHours: number } {
  const now = Date.now();
  const yearAgo = now - 365 * 86400000;
  const sixWeeksAgo = now - 42 * 86400000;

  const endurance = activities.filter(a => {
    const t = new Date(a.start_date_local).getTime();
    const st = toSportType(a.type);
    return t >= yearAgo && st !== "WeightTraining" && st !== "AlpineSki";
  });
  if (endurance.length === 0) return { weeklyHours: 0, longestHours: 0, peakWeeklyHours: 0 };

  // Hours for one activity, guarding against a missing/NaN moving_time (manual
  // entries etc.) — an unguarded NaN would poison the whole volume ramp.
  const hrs = (a: Activity) => (Number.isFinite(a.moving_time) ? a.moving_time : 0) / 3600;

  // Recent 6-week average → current form.
  const recent = endurance.filter(a => new Date(a.start_date_local).getTime() >= sixWeeksAgo);
  const weeklyHours = Math.round((recent.reduce((s, a) => s + hrs(a), 0) / 6) * 4) / 4;

  // Longest single session of the sport over the year.
  const sameSport = endurance.filter(a => toSportType(a.type) === sport);
  const pool = sameSport.length > 0 ? sameSport : endurance;
  const longestHours = Math.round(pool.reduce((m, a) => Math.max(m, hrs(a)), 0) * 4) / 4;

  // Peak sustainable volume: bucket by ISO-ish week, average the 3 biggest.
  const buckets: Record<number, number> = {};
  for (const a of endurance) {
    const wk = Math.floor(new Date(a.start_date_local).getTime() / (7 * 86400000));
    buckets[wk] = (buckets[wk] ?? 0) + hrs(a);
  }
  const top3 = Object.values(buckets).sort((x, y) => y - x).slice(0, 3);
  const peakWeeklyHours = top3.length > 0
    ? Math.round((top3.reduce((s, v) => s + v, 0) / top3.length) * 4) / 4
    : 0;

  return { weeklyHours, longestHours, peakWeeklyHours };
}

// Estimate a session's intensity factor (IF) from whatever data exists:
// power/HR-derived training load first, else average vs max heart rate.
function activityIF(a: Activity): number | null {
  const hours = a.moving_time / 3600;
  if (hours <= 0) return null;
  if (a.icu_training_load != null && a.icu_training_load > 0) {
    return Math.sqrt(a.icu_training_load / (hours * 100));
  }
  if (a.average_heartrate && a.max_heartrate && a.max_heartrate > 0) {
    return a.average_heartrate / a.max_heartrate; // HR-ratio proxy
  }
  return null;
}

// The athlete's HISTORICAL hard fraction over the last year: the share of total
// training load spent at Z4+ (IF ≥ 0.85). Returns null when too few sessions
// can be classified to be meaningful. Load-weighted so long easy sessions and
// short hard sessions are compared fairly.
export function historicalHardFraction(activities: Activity[]): number | null {
  const yearAgo = Date.now() - 365 * 86400000;
  let hardLoad = 0, totalLoad = 0, n = 0;
  for (const a of activities) {
    if (new Date(a.start_date_local).getTime() < yearAgo) continue;
    const st = toSportType(a.type);
    if (st === "WeightTraining" || st === "AlpineSki") continue;
    const ifv = activityIF(a);
    if (ifv == null) continue;
    const hours = a.moving_time / 3600;
    const load = a.icu_training_load && a.icu_training_load > 0
      ? a.icu_training_load
      : ifv * ifv * hours * 100;
    totalLoad += load;
    if (ifv >= 0.85) hardLoad += load;
    n++;
  }
  if (n < 8 || totalLoad <= 0) return null; // not enough signal
  return hardLoad / totalLoad;
}

export function detectSportsFromHistory(
  activities: Activity[],
  events: CalendarEvent[] = [],
): DetectedSport[] {
  const today = new Date().toISOString().slice(0, 10);
  const activityCounts: Partial<Record<SportType, number>> = {};
  const labelCounts: Partial<Record<SportType, Record<string, number>>> = {};

  // Past planned workout events — primary source (richer descriptions)
  for (const e of events) {
    if (e.start_date_local.slice(0, 10) >= today) continue;   // future
    if (e.category && RACE_CAT.test(e.category)) continue;    // skip races
    if (!e.type) continue;
    const sport = toSportType(e.type);
    if (!sport) continue;
    if (!labelCounts[sport]) labelCounts[sport] = {};
    const label = buildEventLabel(e, sport);
    labelCounts[sport]![label] = (labelCounts[sport]![label] ?? 0) + 1;
  }

  // Completed activities — for sport counts + supplement workouts not in events
  for (const a of activities) {
    const sport = toSportType(a.type);
    if (!sport) continue;
    activityCounts[sport] = (activityCounts[sport] ?? 0) + 1;

    // Only add activity-based labels if we have fewer than 5 event-based ones for this sport
    const existing = Object.keys(labelCounts[sport] ?? {}).length;
    if (existing < 5) {
      if (!labelCounts[sport]) labelCounts[sport] = {};
      const label = buildActivityLabel(a, sport);
      labelCounts[sport]![label] = (labelCounts[sport]![label] ?? 0) + 1;
    }
  }

  const allSports = new Set([
    ...Object.keys(activityCounts) as SportType[],
    ...Object.keys(labelCounts) as SportType[],
  ]);

  return ALL_SPORTS
    .filter(s => allSports.has(s))
    .map(s => ({
      sport: s,
      count: activityCounts[s] ?? 0,
      workouts: Object.entries(labelCounts[s] ?? {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([label, count]) => ({ label, count, sport: s })),
    }))
    .sort((a, b) => b.count - a.count);
}

// ── Profile persistence ───────────────────────────────────────────────────────

export function loadProfile(): PlannerProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as any;
      if (!p.setupComplete) return null;
      // Migrate old single-number long/hard to arrays
      if (p.preferredDays) {
        if (typeof p.preferredDays.long === "number") p.preferredDays.long = [p.preferredDays.long];
        if (typeof p.preferredDays.hard === "number") p.preferredDays.hard = [p.preferredDays.hard];
        if (!p.preferredDays.gym) p.preferredDays.gym = [];
      } else {
        p.preferredDays = DEFAULT_PREFERRED_DAYS;
      }
      if (!p.maxWeeklyHours) p.maxWeeklyHours = 8;
      if (!p.planningMode) p.planningMode = "tss";
      if (p.allowDoubles == null) p.allowDoubles = false;
      if (p.doubleThreshold == null) p.doubleThreshold = false;
      if (p.snowStartMonth == null) p.snowStartMonth = 11;
      if (p.snowEndMonth == null) p.snowEndMonth = 4;
      if (!Array.isArray(p.fixedSessions)) p.fixedSessions = [];
      if (!Array.isArray(p.blockedRanges)) p.blockedRanges = [];
      return p as PlannerProfile;
    }
  } catch {}
  return null;
}

export function saveProfile(p: PlannerProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify({
    ...p,
    updatedAt: new Date().toISOString(),
  }));
}

export const DEFAULT_PREFERRED_DAYS: PreferredDays = { long: [5], hard: [1], easy: [0, 3], gym: [] };

export function emptyProfile(): PlannerProfile {
  return {
    sportsEnjoyed: [],
    workoutLibrary: {},
    raceGoals: [],
    goals: "",
    preferredDays: DEFAULT_PREFERRED_DAYS,
    maxWeeklyHours: 8,
    planningMode: "tss",
    allowDoubles: false,
    doubleThreshold: false,
    snowStartMonth: 11,
    snowEndMonth: 4,
    fixedSessions: [],
    blockedRanges: [],
    setupComplete: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// ── Session name picker ───────────────────────────────────────────────────────

export function pickSessionName(
  profile: PlannerProfile,
  sport: SportType,
  slot: "long" | "quality" | "easy",
  fallback: string,
): string {
  const lib = profile.workoutLibrary[sport] ?? [];
  if (lib.length === 0) return fallback;

  const patterns: Record<typeof slot, RegExp> = {
    long:    /\d+h|lång|long|distans/i,
    quality: /interval|tröskel|threshold|fartlek|tempo|backe|sprint/i,
    easy:    /lätt|easy|lugn|recovery|pendling|commute|Z1/i,
  };

  const match = lib.find(n => patterns[slot].test(n));
  return match ?? fallback;
}
